// Sections we request from /cart/add.js so we can render the cart UI from
// a single response instead of issuing follow-up section fetches.
//   - sc-cart-drawer-mobile: new mobile bottom drawer (Agent 2)
//   - cart-icon-bubble:      header cart count (Dawn-style; no DOM target on
//                            this theme today, but harmless if absent)
const PRODUCT_FORM_SECTIONS_TO_RENDER = ['sc-cart-drawer-mobile', 'cart-icon-bubble'];

if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.form.querySelector('[name=id]').disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.buyNowButton = this.querySelector('.product-form__buy-now');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';

        if (this.buyNowButton) {
          this.buyNowButton.addEventListener('click', this.onBuyNowHandler.bind(this));
        }
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        this.performAdd(this.submitButton, { redirectToCheckout: false });
      }

      onBuyNowHandler(evt) {
        evt.preventDefault();
        if (!this.buyNowButton) return;
        this.performAdd(this.buyNowButton, { redirectToCheckout: true });
      }

      performAdd(triggerButton, { redirectToCheckout }) {
        if (triggerButton.getAttribute('aria-disabled') === 'true') return;

        const triggerSpan = triggerButton.querySelector('span');
        const originalText = triggerSpan ? triggerSpan.innerText : '';
        this.handleErrorMessage();

        triggerButton.setAttribute('aria-disabled', true);
        triggerButton.classList.add('loading');
        if (triggerSpan) triggerSpan.innerText = 'Adding to cart...';

        // Per-submission flags. Local (not on `this`) so a previous submission
        // can never strand the next one in a stuck state.
        let addedSuccessfully = false;
        let soldOutSticky = false;

        // Accept: application/json so /cart/add.js returns a normalized JSON
        // error envelope ({status, message, description}) on failure.
        const config = fetchConfig('json');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        // Let the browser set multipart Content-Type for FormData.
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        formData.append('sections', PRODUCT_FORM_SECTIONS_TO_RENDER.join(','));
        formData.append('sections_url', window.location.pathname);
        if (this.cart) this.cart.setActiveElement(document.activeElement);
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description || response.message);

              const soldOutMessage = triggerButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              triggerButton.querySelector('span').classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              soldOutSticky = true;
              return;
            }

            this.error = false;
            addedSuccessfully = true;

            // Swap any cart sections returned in the response so we don't
            // need a follow-up GET /?section_id=... round-trip.
            this.applySectionUpdates(response);

            // Notify subscribers (recipient-form, price-per-item, product-info, etc.)
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'product-form',
              productVariantId: formData.get('id'),
              cartData: response,
            });

            // Preserve Dawn's quick-add modal contract for any host that uses it.
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal && this.cart) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    this.cart.renderContents(response);
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else if (this.cart && typeof this.cart.renderContents === 'function') {
              this.cart.renderContents(response);
            }
          })
          .catch((e) => {
            console.error(e);
            this.handleErrorMessage(
              (window.cartStrings && window.cartStrings.error) ||
                'There was an error adding this item to your cart. Please try again.'
            );
          })
          .finally(() => {
            triggerButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');

            // Always restore the button label and re-enable, unless the success
            // branch swapped in the sticky sold-out UI on this button.
            if (!soldOutSticky) {
              if (triggerSpan) triggerSpan.innerText = originalText;
              triggerButton.removeAttribute('aria-disabled');
            }

            if (!addedSuccessfully) return;

            if (redirectToCheckout) {
              // Buy Now: skip the drawer, head straight to checkout.
              window.location.href = '/checkout';
              return;
            }

            // Wrap openCartUI in a try/catch — if drawer.open() throws (focus
            // management, missing DOM, etc.) the button is already restored
            // above so a second click works, which would manifest as a
            // rage-click. Surface the throw rather than swallow it silently.
            try {
              this.openCartUI();
            } catch (err) {
              console.error('[product-form] openCartUI() threw — drawer did NOT open', err);
            }
          });
      }

      // Parse the `sections` map returned by /cart/add.js (sections=...) and
      // swap each one into the live DOM. Targets the shopify-section wrapper
      // when present so the wrapping <div id="shopify-section-X"> is preserved.
      applySectionUpdates(response) {
        if (!response || !response.sections) return;
        Object.keys(response.sections).forEach((sectionId) => {
          const html = response.sections[sectionId];
          if (!html) return;

          const sectionWrapper = document.getElementById(`shopify-section-${sectionId}`);
          if (sectionWrapper) {
            sectionWrapper.innerHTML = this.extractSectionInner(html, sectionId);
            return;
          }
          // cart-icon-bubble: Dawn uses an <a id="cart-icon-bubble"> rather than
          // a shopify-section wrapper. No-op when the element isn't present.
          const bubble = document.getElementById(sectionId);
          if (bubble) {
            bubble.innerHTML = this.extractSectionInner(html, sectionId);
          }
        });
      }

      extractSectionInner(html, sectionId) {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const wrapper =
          doc.getElementById(`shopify-section-${sectionId}`) || doc.getElementById(sectionId);
        return wrapper ? wrapper.innerHTML : html;
      }

      // Open the new cart drawer on both mobile and desktop. The CSS in
      // sc-cart-drawer-mobile.css switches between a bottom-anchored sheet
      // (<=749px) and a right-anchored slide-out (>=750px). Fall back to the
      // legacy <cart-icon> rerenderCart only if the new drawer isn't on the
      // page yet (rollout-window safety net).
      openCartUI() {
        const drawer = document.querySelector('sc-cart-drawer-mobile');
        if (drawer && typeof drawer.open === 'function') {
          drawer.open();
          return;
        }

        const cartIcon = document.querySelector('cart-icon');
        if (cartIcon && typeof cartIcon.rerenderCart === 'function') {
          cartIcon.rerenderCart();
        }
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }
    }
  );
}
